/**
 * The subcontractor jobsite acknowledgment.
 *
 * This is NOT a lien waiver and must never be treated as one. A statutory
 * waiver releases lien rights against one property for one amount, so it stays
 * per project and per payment. This document is the sub's own acknowledgment
 * of his insurance, his licence, jobsite safety, his lower tier subs,
 * indemnity, incident reporting and the invoicing rules. It is signed ONCE PER
 * SUBCONTRACTOR, covering every job present and future, until a later signed
 * version replaces it. It carries no project address.
 *
 * Like the waivers, the Hub does not write the wording. The office pastes the
 * approved text in Settings, and until it is there, sending and signing refuse
 * with one plain sentence. That refusal stops the ACKNOWLEDGMENT only. Friday's
 * pay run, closeout and commissions are untouched by all of it.
 */

export const ACK_CODE = "jobsite";

export const ACK_TITLE =
  "Subcontractor safety, responsibility, indemnity and release acknowledgment";

/** The one sentence shown, and returned by the server, while the text is empty. */
export const ACK_NOT_ON_FILE =
  "This acknowledgment cannot be prepared yet because its approved wording has not been entered in the Hub. Please call the office.";

/** Blanks the office can mark up inside the pasted text. None are required. */
export const ACK_TOKENS = [
  "{SUBCONTRACTOR}",
  "{TRADE}",
  "{LICENSE}",
  "{CONTRACTOR}",
  "{DATE}",
] as const;

/** Obviously-fake values, used ONLY to preview the pasted text on screen. */
export const ACK_PREVIEW_VALUES: Record<string, string> = {
  "{SUBCONTRACTOR}": "Valley Plumbing Inc.",
  "{TRADE}": "Plumbing",
  "{LICENSE}": "1042288",
  "{CONTRACTOR}": "Dreamliner Construction",
  "{DATE}": "31 July 2026",
};

/** What is printed in a blank the Hub has no value for. Never left empty. */
export const ACK_UNKNOWN = "Not stated";

/**
 * The closing paragraph is a footnote, not a term of the document. It is
 * pulled out of the body so both the screen and the filed PDF can set it as
 * small print, plainly apart from the numbered sections. The split is on the
 * paragraph's own opening words, so editing the wording above it in Settings
 * cannot break it, and a body that no longer contains it simply has none.
 */
export const ACK_FOOTNOTE_OPENING = "For use by Dreamliner Construction";

export function splitAckFootnote(body: string): { terms: string; footnote: string | null } {
  const at = body.indexOf(ACK_FOOTNOTE_OPENING);
  if (at < 0) return { terms: body.trim(), footnote: null };
  return { terms: body.slice(0, at).trim(), footnote: body.slice(at).trim() };
}

/** Fills the office's pasted text. Unknown tokens are left exactly as they are. */
export function fillAckText(body: string, values: Record<string, string>): string {
  return body.replace(/\{[A-Z_]+\}/g, (t) => values[t] ?? t);
}

/** What the sub page and the office file both show about one subcontractor. */
export type SubAcknowledgmentState = {
  /** False while the approved wording has not been pasted in. */
  ready: boolean;
  title: string;
  signed_at: string | null;
  signed_name: string | null;
  version: number | null;
};

/* --------------------------------------------- a phone or an email, always */

/**
 * Six of the eight subcontractors had neither a phone number nor an email
 * address, so nothing the Hub sends could reach them: not the job page, not a
 * waiver, not this. One of the two is now required before somebody is put on a
 * NEW job. No existing record is deleted or invalidated by that. They are
 * flagged, and the office fills them in.
 */
export function subMissingContact(sub: {
  phone?: string | null;
  email?: string | null;
}): boolean {
  return !String(sub.phone ?? "").trim() && !String(sub.email ?? "").trim();
}

export const SUB_CONTACT_REQUIRED =
  "Add a phone number or an email address before putting them on a job. Nothing about the existing record changes, it just needs one of the two so the Hub can reach them.";
